import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { VALUE_STACK, BRAND } from "@/lib/constants";
import { Check } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function ValueStack() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      data-testid="value-stack-section"
      className="bg-[#D8FFB8]/20 py-16 md:py-24 border-y border-[#D8FFB8]/40"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-[#1A1A1A] tracking-tight mb-4">
            {VALUE_STACK.heading}
          </h2>
          <p className="text-[#6B7280] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            {VALUE_STACK.subheading}
          </p>
        </div>

        {/* Value items */}
        <div ref={ref} className="space-y-0 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {VALUE_STACK.items.map((item, i) => (
            <div
              key={item.name}
              data-testid={`value-item-${i}`}
              className={`flex items-start sm:items-center justify-between px-5 md:px-6 py-4 border-b border-gray-100 last:border-b-0 transition-all duration-500 ${
                i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"
              } ${
                isVisible
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-4"
              }`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Check className="h-5 w-5 text-[#20611B] mt-0.5 shrink-0" />
                <div>
                  <span className="font-heading font-semibold text-[#1A1A1A] text-base">
                    {item.name}:
                  </span>{" "}
                  <span className="text-sm text-[#6B7280]">{item.benefit}</span>
                </div>
              </div>
              <div className="ml-4 shrink-0 text-right">
                <span className="text-base font-semibold text-red-400 line-through">
                  {item.isOneTime ? `${item.value}` : item.value}
                </span>
                {item.isOneTime && (
                  <span className="block text-xs text-[#6B7280]">included</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="mt-10 pt-8 text-center space-y-4">
          <p className="text-xl text-[#6B7280] font-heading">
            Total Value:{" "}
            <span className="line-through font-bold text-2xl text-red-400">{VALUE_STACK.totalValue}</span>
          </p>
          <div className="bg-white border-2 border-[#D4A84B] rounded-xl py-6 px-8 inline-block shadow-md">
            <p className="font-heading text-5xl md:text-6xl font-extrabold text-[#D4A84B] leading-none">
              You Pay: {VALUE_STACK.yourPrice}
            </p>
          </div>
          <p className="text-[#6B7280] text-sm max-w-md mx-auto">
            {VALUE_STACK.savingsText}
          </p>
        </div>

        {/* CTA */}
        <div className="text-center mt-8 space-y-3">
          <Link to={BRAND.signupPath}>
            {/* Stripe checkout will connect here */}
            <Button
              data-testid="value-stack-cta"
              className="bg-[#D4A84B] hover:bg-[#B88E3B] text-white font-bold h-14 px-10 rounded-md shadow-md hover:shadow-lg transition-all active:scale-95 text-base tracking-wide"
            >
              {VALUE_STACK.cta}
            </Button>
          </Link>
          <p className="text-sm text-[#6B7280]">{VALUE_STACK.ctaSubtext}</p>
        </div>
      </div>
    </section>
  );
}
